// Path: ./src/app/components/SelectWithAddMui/index.tsx
import * as React from "react";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { styled as MuiStyled, SxProps, Theme } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import { MuiConfirmationModal } from "../MuiConfirmationModal";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { InputField } from "../TextField";

import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import {
  Wrapper,
  Text,
  AddValueLabel,
  TextFieldWrapper,
  TextInputLabel,
} from "./styled";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const StyledTextField = MuiStyled(Select)({
  "& .MuiSelect-select": {
    borderRadius: 10,
    backgroundColor: "white",
    padding: 6,
  },
});

interface Props<T extends string | number> {
  title?: string;
  selectedValue?: T | string | number;
  wrapperStyle?: string;
  selectOptions?: {
    label: string;
    value: T;
  }[];
  FormControlStyle?: Object;
  borderColor?: {
    normal?: string;
    hover?: string;
    focused?: string;
  };
  StyledTextFieldStyle?: SxProps<Theme>;
  // eslint-disable-next-line
  onSelect?: (value: T) => void;
  // eslint-disable-next-line
  onAddNew?: (value: string) => void;
  dataTestId?: string;
}
export function SelectWithAddMui<T extends string | number>({
  title,
  selectOptions,
  wrapperStyle,
  FormControlStyle,
  borderColor,
  StyledTextFieldStyle,
  onSelect,
  selectedValue,
  onAddNew,
  dataTestId,
}: Props<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [inputText, setInputText] = React.useState("");

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const {
      target: { value },
    } = event;

    if (value === "none-value") {
      handleOpen();
    } else {
      onSelect && onSelect(value as T);
    }
  };
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setInputText("");
    // onSelect && onSelect(undefined); //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) This resets the unit when the user clicks the "Cancel" button in the pop-up modal window. need to find a better way to do this
    setOpen(false);
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event?.target.value);
  };
  const handleAdd = () => {
    if (inputText?.trim()?.length) {
      const typedValue = inputText;
      const found = selectOptions?.find(
        (value) => value?.label.toLowerCase() === typedValue.toLowerCase()
      );
      if (found) {
        toastError(
          `${t(messages.Cant_Add_New_Unit_This_Unit_already_Exits())}`
        );
      } else {
        onAddNew && onAddNew(inputText?.toLowerCase());
        toastSuccess(`${t(messages.Added_New_Unit_Successfully())}`);

        setInputText("");
        setOpen(false);
      }
    } else {
      toastError(
        `${t(messages.Cant_Add_New_Unit())}. ${t(
          messages.Please_type_something()
        )}.`
      );
    }
  };

  return (
    <Wrapper wrapperStyle={wrapperStyle} data-testid="selectwithaddmui-test-id">
      {title && <Text>{title}</Text>}{" "}
      <FormControl sx={{ m: 1, width: 150, ...FormControlStyle }}>
        <StyledTextField
          key={selectedValue}
          labelId="name-label"
          id="name"
          value={selectedValue}
          onChange={handleChange}
          MenuProps={MenuProps}
          sx={{
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: borderColor?.normal ? borderColor?.normal : "white",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: borderColor?.hover ? borderColor?.hover : "#fd954f",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: borderColor?.focused
                ? borderColor?.focused
                : "#fd954f",
            },
            ...StyledTextFieldStyle,
          }}
          data-testid={dataTestId}
        >
          {selectOptions?.map((value, key) => (
            <MenuItem
              sx={{
                backgroundColor: "white",
                "& .Mui-selected": {
                  backgroundColor: "red",
                },
                "&:hover .Mui-selected": {
                  backgroundColor: "red",
                },
              }}
              key={key}
              value={value.value}
              data-testid={`${dataTestId}-option`}
            >
              {value.label}
            </MenuItem>
          ))}
          {onAddNew && (
            <MenuItem
              sx={{
                letterSpacing: "0px",
                color: "#f58220",
                textTransform: "capitalize",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "white",
                "& .Mui-selected": {
                  backgroundColor: "red",
                },
                "&:hover .Mui-selected": {
                  backgroundColor: "red",
                },
              }}
              key={"none-value"}
              value={"none-value"}
            >
              <AddValueLabel>{`${t(messages.Add_New())}`}</AddValueLabel>
              <AddIcon></AddIcon>
            </MenuItem>
          )}
        </StyledTextField>
      </FormControl>
      <MuiConfirmationModal
        open={open}
        handleCancel={handleClose}
        handleConfirm={handleAdd}
        title={`${t(messages.Add_Unit())}`}
        description={
          <TextFieldWrapper>
            <TextInputLabel>{t(messages.Unit())}</TextInputLabel>
            <InputField
              type="text"
              placeholder={t(messages.Type_here()) as string}
              onChange={handleInputChange}
            />
          </TextFieldWrapper>
        }
      />
    </Wrapper>
  );
}
